import { useCallback, useMemo, useRef, useState } from 'react';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { AddPaymentOptionParams, UpdatePaymentOptionParams, UseBilling } from '../useBilling';
import { CompletePurchaseParams, UsePayment, UsePaywall } from '../types';
import { useTranslation } from '../../I18N';

export class ThreeDFailure extends Error {}

export interface ThreeDSecureErrorData {
  data?: {
    errorData?: {
      threeDSecureActionTokenId?: string;
      publicData?: {
        threeDSecureActionTokenId?: string;
      };
    };
  };
}

export enum ThreeDActionType {
  ADD_PAYMENT = 'add_payment',
  UPDATE_PAYMENT = 'update_payment',
  PURCHASE = 'purchase',
}

type ThreeDActionContext = {
  token?: string;
  actionType?: ThreeDActionType;
  selectedBillingInfoId?: string;
};

export type ThreeDActionTokenResult = {
  actionType?: ThreeDActionType;
  card?: UseBilling['wallet'][0];
};

type ThreeDActionParams = AddPaymentOptionParams | UpdatePaymentOptionParams | CompletePurchaseParams;

type UseThreeDSecureFlowParams = {
  onAddPaymentOption: UseBilling['onAddPaymentOption'];
  onUpdatePaymentOption: UseBilling['onUpdatePaymentOption'];
  onCompletePurchase?: UsePayment['completePurchase'];
  onNext?: UsePaywall['onNext'];
  setSelectedCard?: (card: UseBilling['wallet'][0]) => void;
  setVisibleError: (value: string) => void;
  isSingleClickReactivation?: boolean;
  skipToLastStep?: UsePaywall['skipToLastStep'];
  moveToCheckoutStep?: UsePaywall['moveToCheckoutStep'];
};

const getErrorMessageTranslationKey = (actionType: ThreeDActionType, isSingleClickReactivation?: boolean) => {
  if (actionType === ThreeDActionType.ADD_PAYMENT || actionType === ThreeDActionType.UPDATE_PAYMENT)
    return 'common:billing.card.added.failure';

  if (isSingleClickReactivation) return 'common:paywall.payment.paymentMethod.error';

  return 'common:paywall.payment.error';
};

export const useThreeDSecureFlow = ({
  onAddPaymentOption,
  onCompletePurchase,
  onUpdatePaymentOption,
  onNext,
  setSelectedCard,
  setVisibleError,
  isSingleClickReactivation,
  moveToCheckoutStep,
  skipToLastStep,
}: UseThreeDSecureFlowParams) => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();

  const [threeDActionContext, setThreeDActionContext] = useState<ThreeDActionContext>();
  const [threeDActionError, setThreeDActionError] = useState<Error>();

  // We hold any older payment information here when we need to do the extra hop for 3D secure token:
  const preThreeDConfirmationParams = useRef<ThreeDActionParams>();

  const handle3DSError = useCallback(
    ({
      errorData,
      actionType,
      actionParams,
    }: {
      errorData: ThreeDSecureErrorData;
      actionType: ThreeDActionType;
      actionParams: ThreeDActionParams;
    }) => {
      // There can be an error if we need to use the 3-D Secure flow
      const threeDToken: string =
        errorData?.data?.errorData?.threeDSecureActionTokenId ||
        errorData?.data?.errorData?.publicData?.threeDSecureActionTokenId;

      if (threeDToken) {
        // Save the previous information for when we attempt to finish the 3D secure flow
        // We will call this same function with only the threeDToken so we save them for the next round
        Object.assign(preThreeDConfirmationParams, {
          current: actionParams,
        });
        setThreeDActionContext({ token: threeDToken, actionType });
      } else {
        showMessage({
          message: t(getErrorMessageTranslationKey(actionType, isSingleClickReactivation)),
          color: 'paywallError',
        });
        if (isSingleClickReactivation && actionType === ThreeDActionType.PURCHASE) {
          moveToCheckoutStep?.();
        }
      }
    },
    [showMessage, moveToCheckoutStep, t, isSingleClickReactivation]
  );

  const onAddPaymentOptionWith3DS: UseBilling['onAddPaymentOption'] = useCallback(
    async (params) => {
      try {
        const addPaymentResult = await onAddPaymentOption(params);
        setSelectedCard?.(addPaymentResult);
        return addPaymentResult;
      } catch (error) {
        handle3DSError({ errorData: error, actionType: ThreeDActionType.ADD_PAYMENT, actionParams: params });
      }
      return null;
    },
    [handle3DSError, onAddPaymentOption, setSelectedCard]
  );

  const onUpdatePaymentOptionWith3DS: UseBilling['onUpdatePaymentOption'] = useCallback(
    async (params) => {
      try {
        const updatePaymentResult = await onUpdatePaymentOption(params);
        setSelectedCard?.(updatePaymentResult);
        return updatePaymentResult;
      } catch (error) {
        handle3DSError({ errorData: error, actionType: ThreeDActionType.UPDATE_PAYMENT, actionParams: params });
      }
      return null;
    },
    [handle3DSError, onUpdatePaymentOption, setSelectedCard]
  );

  const onCompletePurchaseWith3DS: UsePayment['completePurchase'] = useCallback(
    async (params) => {
      try {
        await onCompletePurchase?.(params);
        showMessage({
          message: t('common:paywall.payment.success'),
          color: 'success',
        });
        if (isSingleClickReactivation) {
          skipToLastStep?.();
        } else {
          onNext?.();
        }
      } catch (error) {
        handle3DSError({
          errorData: error?.graphQLErrors?.[0],
          actionType: ThreeDActionType.PURCHASE,
          actionParams: params,
        });
      }
    },
    [handle3DSError, onCompletePurchase, onNext, skipToLastStep, showMessage, t, isSingleClickReactivation]
  );

  const onThreeDActionError = useCallback(() => {
    // We clear the information we have stored for the 3D secure flow
    setThreeDActionContext(undefined);
    showMessage({
      color: 'paywallError',
      message: t('common:paywall.payment.error'),
    });
    setVisibleError('3D Secure flow failed [3001]');
    setThreeDActionError(new ThreeDFailure());
  }, [setVisibleError, showMessage, t]);

  const onThreeDActionToken = useCallback(
    async (token): Promise<ThreeDActionTokenResult> => {
      const actionType = threeDActionContext?.actionType;
      let card: UseBilling['wallet'][0];
      try {
        if (actionType === ThreeDActionType.ADD_PAYMENT) {
          const addedPaymentOption = await onAddPaymentOption({
            threeDSecureActionResultTokenId: token.id,
            ...(preThreeDConfirmationParams?.current as AddPaymentOptionParams),
          });
          if (addedPaymentOption) {
            setSelectedCard?.(addedPaymentOption);
            card = addedPaymentOption;
          }
        }

        if (actionType === ThreeDActionType.UPDATE_PAYMENT) {
          const updatedPaymentOption = await onUpdatePaymentOption({
            billingInfoId: threeDActionContext?.selectedBillingInfoId,
            threeDSecureActionResultTokenId: token.id,
            ...(preThreeDConfirmationParams?.current as UpdatePaymentOptionParams),
          });
          if (updatedPaymentOption) {
            setSelectedCard?.(updatedPaymentOption);
            card = updatedPaymentOption;
          }
        }

        if (actionType === ThreeDActionType.PURCHASE) {
          await onCompletePurchase({
            threeDSecureActionResultTokenId: token.id,
            termsAccepted: true,
            ...(preThreeDConfirmationParams?.current as CompletePurchaseParams),
          });
          showMessage({
            message: t('common:paywall.payment.success'),
            color: 'success',
          });
          onNext();
        }

        Object.assign(preThreeDConfirmationParams, {
          current: null,
        });
        setThreeDActionContext(null);
      } catch (error) {
        // we do not want to display the error notification if the 3DS flow is required

        if (
          error?.graphQLErrors?.[0]?.data?.errorData?.threeDSecureActionTokenId ||
          error?.data?.errorData?.threeDSecureActionTokenId ||
          error?.data?.errorData?.publicData?.threeDSecureActionTokenId
        ) {
          // Another 3DS round is required — nothing completed yet, so no card to report.
          return { actionType };
        }

        onThreeDActionError();
        return { actionType };
      }
      return { actionType, card };
    },
    [
      threeDActionContext,
      onAddPaymentOption,
      setSelectedCard,
      onUpdatePaymentOption,
      onCompletePurchase,
      showMessage,
      t,
      onNext,
      onThreeDActionError,
    ]
  );

  return useMemo(
    () => ({
      onAddPaymentOptionWith3DS,
      onUpdatePaymentOptionWith3DS,
      onCompletePurchaseWith3DS,
      threeDActionContext,
      setThreeDActionContext,
      threeDActionError,
      setThreeDActionError,
      preThreeDConfirmationParams,
      onThreeDActionError,
      onThreeDActionToken,
    }),
    [
      threeDActionContext,
      setThreeDActionContext,
      threeDActionError,
      setThreeDActionError,
      preThreeDConfirmationParams,
      onThreeDActionError,
      onAddPaymentOptionWith3DS,
      onUpdatePaymentOptionWith3DS,
      onCompletePurchaseWith3DS,
      onThreeDActionToken,
    ]
  );
};

export type UseThreeDSecureFlow = ReturnType<typeof useThreeDSecureFlow>;
